import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Tab, Tabs } from '@mui/material';
import { History, MonitorHeart, PriceCheck } from '@mui/icons-material';
import LiveOpsTab from './LiveOpsTab';
import HistoryTab from './HistoryTab';
import PricingAccuracyTab from './PricingAccuracyTab';
import { Page, PageHeader } from '../../components/layout/Page';

const TABS = [
  { key: 'live', label: 'Live Ops', icon: <MonitorHeart />, Component: LiveOpsTab },
  { key: 'history', label: 'History', icon: <History />, Component: HistoryTab },
  { key: 'pricing', label: 'Pricing accuracy', icon: <PriceCheck />, Component: PricingAccuracyTab },
] as const;

/**
 * /tracking: is mail coming in and getting answered (Live Ops), how the last
 * weeks went (History), and how often the system's price went out (Pricing
 * accuracy). Only the open tab is mounted, so Live Ops polls only while it
 * is on screen and the other tabs query only when opened. The tab is in the
 * URL (?view=history) so a reload or a shared link keeps it.
 */
const TrackingPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const index = Math.max(
    0,
    TABS.findIndex((t) => t.key === params.get('view')),
  );
  const { Component } = TABS[index];

  return (
    <Page width="wide">
      <Box>
        <PageHeader
          title="Tracking"
          subtitle={<>
            Is mail coming in and getting answered? The open queue right now, how the last weeks went, and how often the system’s price was the one sent.
            Times are Singapore time.
          </>}
        />

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={index}
            onChange={(_, i: number) => setParams(i === 0 ? {} : { view: TABS[i].key }, { replace: true })}
            aria-label="Tracking views"
            variant="scrollable"
            allowScrollButtonsMobile
            // Phones: no icons and tighter tabs, so all three fit at 390px instead of hiding one behind a scroll arrow.
            sx={{ '& .MuiTab-root': { minWidth: { xs: 0, sm: 90 }, px: { xs: 1.25, sm: 2 } }, '& .MuiTab-iconWrapper': { display: { xs: 'none', sm: 'inline-flex' } } }}
          >
            {TABS.map((t, i) => (
              <Tab key={t.key} label={t.label} icon={t.icon} iconPosition="start" id={`tracking-tab-${i}`} aria-controls="tracking-tabpanel" />
            ))}
          </Tabs>
        </Box>

        <Box role="tabpanel" id="tracking-tabpanel" aria-labelledby={`tracking-tab-${index}`}>
          <Component />
        </Box>
      </Box>
    </Page>
  );
};

export default TrackingPage;
